import { IconDefinition } from "@fortawesome/fontawesome-svg-core"
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from "@mui/material"
import { FORM_TAB_BOXES } from "form/model"
import { getIsTabBoxOpened, rememberTabBoxIsOpened } from "form/tools/tab-boxes"
import React from "react"
import { FlexBox } from "ui/components/FlexBox"

interface IContentBoxLayoutProps {
  id: FORM_TAB_BOXES
  title: string
  subTitle?: string
  isDesktopScreen?: boolean
  fullHeight?: boolean
  sxChildren?: any
  icon?: IconDefinition
}

interface IContentBoxLayoutState {
  isExpanded: boolean
}

export class ContentBoxLayout extends React.PureComponent<IContentBoxLayoutProps, IContentBoxLayoutState> {
  public constructor(props: IContentBoxLayoutProps) {
    super(props)
    this.state = {
      isExpanded: getIsTabBoxOpened(props.id)
    }
  }

  public render() {
    const { id, title, subTitle, isDesktopScreen, children, sxChildren, fullHeight, icon } = this.props
    return (
      <Accordion
        id={id}
        expanded={fullHeight || getIsTabBoxOpened(id)}
        onChange={(_event: React.SyntheticEvent, isExpanded: boolean) => {
          this.setState({ isExpanded })
          rememberTabBoxIsOpened(id, isExpanded)
        }}
        sx={{
          "height": fullHeight ? "100%" : undefined,
          "minHeight": fullHeight ? "100%" : undefined,
          "maxHeight": fullHeight ? "100%" : undefined,
          "boxShadow":
            "1px 2px 1px -1px rgb(0 0 0 / 20%), 0px 0px 3px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)",
          "backgroundColor": theme => theme.palette.background.default,
          "&.MuiPaper-root": {
            mt: 0,
            mb: "1rem",
            border: theme => theme.palette.border,
            borderRadius: ".25rem",
            boxShadow: "none",
            overflow: "hidden"
          },
          "& .MuiAccordionSummary-content": { mt: ".8rem", mb: 0 },
          "& .MuiAccordionSummary-content.Mui-expanded": {
            mt: ".8rem"
          },
          "& .MuiAccordionDetails-root, & .MuiAccordionSummary-root": {
            pl: "2rem",
            pr: "2rem"
          },
          "& .MuiAccordionSummary-root": {
            minHeight: "5rem",
            alignItems: "start"
          },
          "& .MuiAccordionSummary-root:hover": {
            cursor: fullHeight ? "auto" : "pointer"
          },
          "& .MuiAccordionDetails-root": {
            pb: fullHeight ? undefined : "2rem"
          },
          "& .MuiCollapse-root": {
            borderTop: "none"
          },
          "& .MuiAccordionSummary-expandIconWrapper": {
            mt: "1.5rem",
            visibility: fullHeight ? "hidden" : undefined
          },
          "& .MuiCollapse-vertical": {
            height: fullHeight ? "calc(100% - 5rem)" : undefined
          },
          "& .MuiCollapse-vertical:first-of-type": {
            height: fullHeight ? "100%" : undefined
          },
          "& .MuiAccordion-region, & .MuiAccordionDetails-root": {
            height: fullHeight ? "100%" : undefined
          }
        }}>
        <AccordionSummary expandIcon={<FontAwesomeIcon id={`${id}-expand-button`} icon={faChevronDown} />}>
          <FlexBox vertical ai="start" sx={{ pt: ".5rem", pb: ".5rem", width: "calc(100% - 1rem)" }}>
            <FlexBox jc="space-between" sx={{ width: "calc(100% - 1rem)" }}>
              <FlexBox gap="1rem">
                {icon && <FontAwesomeIcon icon={icon} style={{ marginLeft: "1rem" }} />}
                <Typography id={`${id}-title`} color="textPrimary" fontWeight={500}>
                  {title}
                </Typography>
              </FlexBox>
              {isDesktopScreen && subTitle && (
                <Typography
                  id={`${id}-subtitle`}
                  sx={{
                    maxWidth: "40%",
                    textAlign: "end",
                    fontSize: ".8rem",
                    color: theme => theme.palette.text.secondary
                  }}>
                  {subTitle}
                </Typography>
              )}
            </FlexBox>
            <Box
              sx={{
                width: "4rem",
                mt: ".6rem",
                height: 0,
                borderBottom: theme => `3px solid ${theme.palette.primary.main}`
              }}
            />
          </FlexBox>
        </AccordionSummary>
        <AccordionDetails id={`${id}-content`} sx={{ ...sxChildren }}>
          {children}
        </AccordionDetails>
      </Accordion>
    )
  }
}
